import { motion } from 'framer-motion';
import { AlertCircle, Loader2, ShieldCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../config/axiosConfig';

// ── Composants réutilisables du style HUD ─────────────────────────────
const PerspectiveGrid = () => (
  <div
    style={{
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      backgroundImage: `
        linear-gradient(to right, rgba(34,211,238,0.06) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(245,158,11,0.06) 1px, transparent 1px)
      `,
      backgroundSize: '80px 80px',
      maskImage: 'radial-gradient(circle at 50% 50%, black 40%, transparent 100%)',
    }}
  />
);

const ScanLine = () => (
  <motion.div
    animate={{ top: ['-2%', '102%'] }}
    transition={{ duration: 4.5, repeat: Infinity, ease: 'linear', repeatDelay: 1 }}
    style={{
      position: 'absolute',
      left: 0,
      right: 0,
      height: '1px',
      background: 'linear-gradient(90deg, transparent 0%, rgba(34,211,238,0.5) 30%, rgba(245,158,11,0.5) 70%, transparent 100%)',
      zIndex: 10,
      pointerEvents: 'none',
    }}
  />
);

// ── Écran principal ─────────────────────────────────────────────────────
const EmailVerificationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [status, setStatus] = useState('loading'); // loading | success | error | expired
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const hasProcessed = useRef(false);
  const [isResending, setIsResending] = useState(false);

  const query = new URLSearchParams(location.search);
   

  const getTokenFromUrl = () => {
    const params = new URLSearchParams(location.search);
    let token = params.get('token');

    // Fallback : essayer de lire directement window.location
    if (!token && typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      token = urlParams.get('token');
    }

    return token;
  };

  const token = getTokenFromUrl();

  useEffect(() => {
    if (!token) {
      if (!hasProcessed.current) {
        hasProcessed.current = true;
        setStatus('error');
        setMessage("Token manquant dans l'URL. Le lien semble corrompu.");
        toast.error("Token manquant – veuillez réessayer depuis l'email");
      }
      return;
    }

    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const verify = async () => {
      try {
        const response = await api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);

        setStatus('success');
        setMessage(response.data.message || "Email vérifié avec succès !");
        
        toast.success("Email vérifié avec succès");
        
        setTimeout(() => navigate('/auth/login', { replace: true }), 5000);

      } catch (err) {
        const errMsg = err?.response?.data?.message || "Erreur de vérification";
        
        if (err?.response?.status === 410) {
          setStatus('expired');
        } else {
          setStatus('error');
        }
        
        setMessage(errMsg);
        toast.error(errMsg);
      }
    };

    verify();
  }, [token, navigate]);

  // ── Redirection manuelle ───────────────────────────────────────────────
  const handleGoToLogin = () => navigate('/auth/login', { replace: true });
  const handleGoToDashboard = () => navigate('/dashboard', { replace: true });
  const handleResendVerification = async () => {
    if (!email) {
      toast.error("Email non disponible");
      return;
    }

    setIsResending(true);
    try {
      await api.post('/auth/resend-verification', { email });
      toast.success("Un nouvel email de vérification a été envoyé à " + email);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Impossible de renvoyer l'email");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#020617',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: 'monospace',
        color: '#e2e8f0',
      }}
    >
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(circle at 30% 20%, rgba(34,211,238,0.04) 0%, transparent 50%)' }} />
      <PerspectiveGrid />

      <div style={{ position: 'absolute', inset: 0, opacity: 0.025, pointerEvents: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")` }} />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        style={{
          width: '100%',
          maxWidth: 460,
          padding: '0 1.5rem',
          zIndex: 20,
        }}
      >
        <div
          style={{
            position: 'relative',
            background: 'rgba(2,6,23,0.85)',
            backdropFilter: 'blur(24px)',
            border: '1px solid rgba(255,255,255,0.06)',
            overflow: 'hidden',
            padding: '3rem 2.5rem',
          }}
        >
          <ScanLine />

          {/* Coins décoratifs */}
          <span style={{ position: 'absolute', top: -1, left: -1, width: 14, height: 14, borderTop: '2px solid #22d3ee', borderLeft: '2px solid #22d3ee' }} />
          <span style={{ position: 'absolute', bottom: -1, right: -1, width: 14, height: 14, borderBottom: '2px solid #f59e0b', borderRight: '2px solid #f59e0b' }} />

          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              {status === 'loading' && <Loader2 size={48} className="animate-spin mx-auto" style={{ color: '#22d3ee' }} />}
              {status === 'success' && <ShieldCheck size={48} style={{ color: '#10b981', margin: '0 auto' }} />}
              {(status === 'error' || status === 'expired') && <AlertCircle size={48} style={{ color: '#ef4444', margin: '0 auto' }} />}
            </div>

            <h1 style={{ fontSize: '1.1rem', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>
              {status === 'loading' && "VÉRIFICATION EN COURS"}
              {status === 'success' && "SÉQUENCE VALIDÉE"}
              {status === 'expired' && "LIEN EXPIRED"}
              {status === 'error' && "ÉCHEC DE VÉRIFICATION"}
            </h1>
          </div>

          <p
            style={{
              fontSize: '0.78rem',
              lineHeight: 1.7,
              color: status === 'success' ? '#86efac' : 'rgba(255,255,255,0.75)',
              textAlign: 'center',
              marginBottom: '2.5rem',
            }}
          >
            {message || "Traitement de la séquence de vérification..."}
          </p>

          {/* Boutons d'action */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {status === 'success' && (
              <motion.button
                onClick={handleGoToLogin}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: 'linear-gradient(135deg, #10b981, #34d399)',
                  color: '#020617',
                  border: 'none',
                  fontFamily: 'monospace',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  borderRadius: '4px',
                }}
              >
                SE CONNECTER →
              </motion.button>
            )}

            {(status === 'error' || status === 'expired') && (
              <>
                <motion.button
                  onClick={handleResendVerification}
                  disabled={isResending}
                  whileHover={!isResending ? { scale: 1.03 } : {}}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: 'transparent',
                    color: '#f59e0b',
                    border: '1px solid #f59e0b',
                    fontFamily: 'monospace',
                    fontSize: '0.62rem',
                    letterSpacing: '0.2em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                  }}
                >
                  RENVOYER L'EMAIL DE VÉRIFICATION
                </motion.button>
              </>
            )}
          </div>
        </div>

        {/* Métadonnées */}
        <div style={{ marginTop: '1.5rem', textAlign: 'center', opacity: 0.4, fontSize: '0.45rem', letterSpacing: '0.15em' }}>
          PESA<span style={{ color: '#22d3ee' }}>CORE</span> • EMAIL_VERIFICATION_PROTOCOL
        </div>
      </motion.div>
    </div>
  );
};

export default EmailVerificationPage;